import { useEffect, useRef } from "react";
import nativeWebSocket from "../websocket-native";
import { coincideBusquedaTraslado } from "../utils/busquedaTraslado";

/**
 * Hook para WebSocket nativo - sin Laravel Echo
 */
const useNativeWebSocket = (traslados, setTraslados, fechaInicio = null, fechaFin = null, filtros = {}) => {
  // Usar refs para mantener fechas y filtros actualizados sin causar reconexiones
  const fechaInicioRef = useRef(fechaInicio);
  const fechaFinRef = useRef(fechaFin);
  const filtrosRef = useRef(filtros);

  // Actualizar refs cuando cambien las fechas y filtros
  useEffect(() => {
    fechaInicioRef.current = fechaInicio;
    fechaFinRef.current = fechaFin;
    filtrosRef.current = filtros;
  }, [fechaInicio, fechaFin, filtros]);
  // Orden cronológico como Centro de Control: fecha (ASC), hora_recogida (ASC) y luego ID (DESC).
  // Antes solo usaba la hora y mezclaba los días en listas de varias fechas (Autorizaciones > Ver traslados)
  const ordenarTraslados = (traslados) => {
    return traslados.sort((a, b) => {
      const fechaA = String(a.fecha || '').slice(0, 10);
      const fechaB = String(b.fecha || '').slice(0, 10);
      if (fechaA !== fechaB) {
        return fechaA.localeCompare(fechaB);
      }

      // Luego por hora_recogida (más temprana primero)
      const horaA = a.hora_recogida || '00:00:00';
      const horaB = b.hora_recogida || '00:00:00';
      
      if (horaA !== horaB) {
        return horaA.localeCompare(horaB);
      }
      
      // Si las horas son iguales, ordenar por ID (más reciente primero)
      return b.id - a.id;
    });
  };

  // Función para validar si un traslado cumple con los filtros activos
  const validarFiltrosTraslado = (traslado) => {
    const filtros = filtrosRef.current;

    // IMPORTANTE: Validar zonas permitidas primero (restricción de seguridad basada en rol)
    // Si zonasPermitidas es un array (hay restricción), verificar que la zona del traslado esté permitida
    if (filtros.zonasPermitidas && Array.isArray(filtros.zonasPermitidas) && filtros.zonasPermitidas.length > 0) {
      if (!filtros.zonasPermitidas.includes(traslado.zona_paciente)) {
        return false; // Rechazar traslados de zonas no permitidas
      }
    }

    // Validar fecha
    if (fechaInicioRef.current && fechaFinRef.current && traslado.fecha) {
      const fechaTrasladoStr = traslado.fecha.split('T')[0];
      const fechaInicioStr = fechaInicioRef.current.format('YYYY-MM-DD');
      const fechaFinStr = fechaFinRef.current.format('YYYY-MM-DD');

      if (fechaTrasladoStr < fechaInicioStr || fechaTrasladoStr > fechaFinStr) {
        return false;
      }
    }

    // Validar zona seleccionada en el filtro (adicional a las zonas permitidas)
    if (filtros.selectedZona && traslado.zona_paciente !== filtros.selectedZona) {
      return false;
    }
    
    // Validar estados seleccionados
    if (filtros.selectedStatuses && filtros.selectedStatuses.length > 0 && filtros.selectedStatuses.length < 11) {
      if (!filtros.selectedStatuses.includes(traslado.status)) {
        return false;
      }
    }
    
    // Validar seguimiento/validación
    if (filtros.selectedValidacion && filtros.selectedValidacion !== "Todos" && filtros.selectedValidacion !== "Ambos") {
      if (traslado.seguimiento !== filtros.selectedValidacion) {
        return false;
      }
    }
    
    // Validar confirmación
    if (filtros.selectedConfirmacion && filtros.selectedConfirmacion !== "Todos" && filtros.selectedConfirmacion !== "Ambos") {
      if (traslado.confirmacion !== filtros.selectedConfirmacion) {
        return false;
      }
    }
    
    // Validar copago
    if (filtros.selectedCopago && filtros.selectedCopago.length < 2) {
      const tieneCopago = traslado.copago > 0 ? "con_copago" : "sin_copago";
      if (!filtros.selectedCopago.includes(tieneCopago)) {
        return false;
      }
    }
    
    // Validar móvil/vehículo
    if (filtros.selectedVehicleId && traslado.vehiculo_id !== filtros.selectedVehicleId) {
      return false;
    }
    
    // Validar departamento y ciudad usando los nombres
    // Solo validar si ambos están seleccionados (departamento Y ciudad)
    if (filtros.selectedDepartamento && filtros.selectedCiudad &&
        filtros.selectedDepartamentoNombre && filtros.selectedCiudadNombre) {
      const depNombre = filtros.selectedDepartamentoNombre.toLowerCase();
      const ciudadNombre = filtros.selectedCiudadNombre.toLowerCase();

      // Verificar si el traslado pertenece al departamento y ciudad seleccionados
      // El traslado debe coincidir en origen O destino
      const origenDep = (traslado.origen_departamento || '').toLowerCase();
      const origenCiudad = (traslado.origen_ciudad || '').toLowerCase();
      const destinoDep = (traslado.destino_departamento || '').toLowerCase();
      const destinoCiudad = (traslado.destino_ciudad || '').toLowerCase();

      const coincideOrigen = origenDep === depNombre && origenCiudad === ciudadNombre;
      const coincideDestino = destinoDep === depNombre && destinoCiudad === ciudadNombre;

      if (!coincideOrigen && !coincideDestino) {
        return false;
      }
    }

    // Validar hora
    if (filtros.selectedHour && traslado.hora_recogida && typeof traslado.hora_recogida === 'string') {
      const horaTraslado = traslado.hora_recogida.slice(0, 5);
      const horaFiltro = filtros.selectedHour.toTimeString?.()?.slice(0, 5) || '';
      if (horaTraslado !== horaFiltro) {
        return false;
      }
    }

    // Validar EPS
    if (filtros.selectedEps && filtros.selectedEps !== "") {
      if (traslado.empresa !== filtros.selectedEps) {
        return false;
      }
    }

    // Validar término de búsqueda
    if (!coincideBusquedaTraslado(traslado, filtros.searchTermTraslados)) {
      return false;
    }
    
    return true;
  };

  useEffect(() => {
    // No necesitamos conectar aquí porque WebSocketProvider lo maneja globalmente
    
    // Registrar callback para actualizaciones de traslados
    nativeWebSocket.onTrasladoUpdate((data) => {
      const { trasladoId, field, value } = data;
      
      if (!trasladoId || !field || value === undefined) {
        return;
      }
      
      // Actualizar estado
      setTraslados(prevTraslados => {
        if (!Array.isArray(prevTraslados)) {
          return prevTraslados;
        }
        
        const updated = prevTraslados.map(traslado => {
          if (traslado.id === trasladoId) {
            const trasladoActualizado = {
              ...traslado,
              [field]: value
            };
            
            // Validar si el traslado actualizado cumple con los filtros activos
            if (validarFiltrosTraslado(trasladoActualizado)) {
              return trasladoActualizado;
            } else {
              // Si ya no cumple con los filtros, marcarlo para eliminación
              return null;
            }
          }
          return traslado;
        }).filter(t => t !== null); // Remover traslados que ya no cumplen filtros
        
        return ordenarTraslados(updated);
      });
    });

    // Registrar callback para creación de traslados
    nativeWebSocket.onTrasladoCreated((data) => {
      const { trasladoId, traslado } = data;
      
      if (!trasladoId || !traslado) {
        return;
      }

      // Validar si el traslado cumple con todos los filtros activos
      if (!validarFiltrosTraslado(traslado)) {
        return;
      }
      
      // Agregar el nuevo traslado al estado actual
      setTraslados(prevTraslados => {
        if (!Array.isArray(prevTraslados)) {
          console.error("❌ prevTraslados no es array:", prevTraslados);
          return prevTraslados;
        }
        
        // Verificar si el traslado ya existe (evitar duplicados)
        const existeTraslado = prevTraslados.some(t => t.id === trasladoId);
        if (existeTraslado) {
          return prevTraslados;
        }
        
        // Agregar el nuevo traslado y ordenar correctamente
        const nuevaLista = [...prevTraslados, traslado];
        
        return ordenarTraslados(nuevaLista);
      });
    });

    // Registrar callback para cambios de estado de traslados
    nativeWebSocket.onTrasladoStatusChange((data) => {
      const { trasladoId, traslado } = data;
      
      if (!trasladoId || !traslado) {
        console.warn("⚠️ Datos incompletos en cambio de estado:", data);
        return;
      }
      
      // Actualizar el traslado completo en el estado
      setTraslados(prevTraslados => {
        if (!Array.isArray(prevTraslados)) {
          console.error("❌ prevTraslados no es array:", prevTraslados);
          return prevTraslados;
        }
        
        const updated = prevTraslados.map(t => {
          if (t.id === trasladoId) {
            const trasladoActualizado = { ...t, ...traslado };
            
            // Validar si el traslado actualizado cumple con los filtros activos
            if (validarFiltrosTraslado(trasladoActualizado)) {
              return trasladoActualizado;
            } else {
              // Si ya no cumple con los filtros, marcarlo para eliminación
              return null;
            }
          }
          return t;
        }).filter(t => t !== null); // Remover traslados que ya no cumplen filtros
        
        // Si el traslado no estaba en la lista pero ahora cumple los filtros, agregarlo
        const existeTraslado = prevTraslados.some(t => t.id === trasladoId);
        if (!existeTraslado && validarFiltrosTraslado(traslado)) {
          const nuevaLista = [...updated, traslado];
          return ordenarTraslados(nuevaLista);
        }
        
        return ordenarTraslados(updated);
      });
    });

    // Registrar callback para actualizaciones completas de traslados (incluye estados especiales)
    nativeWebSocket.onTrasladoActualizado((data) => {
      const { trasladoId, traslado } = data;
      
      if (!trasladoId || !traslado) {
        console.warn("⚠️ Datos incompletos en actualización completa:", data);
        return;
      }
      
      // Actualizar el traslado completo en el estado
      setTraslados(prevTraslados => {
        if (!Array.isArray(prevTraslados)) {
          console.error("❌ prevTraslados no es array:", prevTraslados);
          return prevTraslados;
        }
        
        const updated = prevTraslados.map(t => {
          if (t.id === trasladoId) {
            const trasladoActualizado = { ...t, ...traslado };
            
            // Validar si el traslado actualizado cumple con los filtros activos
            if (validarFiltrosTraslado(trasladoActualizado)) {
              return trasladoActualizado;
            } else {
              // Si ya no cumple con los filtros, marcarlo para eliminación
              return null;
            }
          }
          return t;
        }).filter(t => t !== null); // Remover traslados que ya no cumplen filtros
        
        // Si el traslado no estaba en la lista pero ahora cumple los filtros, agregarlo
        const existeTraslado = prevTraslados.some(t => t.id === trasladoId);
        if (!existeTraslado && validarFiltrosTraslado(traslado)) {
          const nuevaLista = [...updated, traslado];
          return ordenarTraslados(nuevaLista);
        }
        
        return ordenarTraslados(updated);
      });
    });

    // Registrar callback para actualizaciones de contador de observaciones
    nativeWebSocket.onObservacionesUpdate((data) => {
      const { trasladoId, observaciones_count } = data;
      
      if (!trasladoId || observaciones_count === undefined) {
        console.warn("⚠️ Datos incompletos en actualización de observaciones:", data);
        return;
      }
      
      // Actualizar solo el contador de observaciones del traslado específico
      setTraslados(prevTraslados => {
        if (!Array.isArray(prevTraslados)) {
          console.error("❌ prevTraslados no es array:", prevTraslados);
          return prevTraslados;
        }
        
        const updated = prevTraslados.map(t => {
          if (t.id === trasladoId) {
            return { ...t, observaciones_count };
          }
          return t;
        });
        
        return updated;
      });
    });
    
    // Cleanup
    return () => {
      nativeWebSocket.disconnect();
    };
  }, [setTraslados]); // Solo dependiente de setTraslados, no de las fechas
};

export default useNativeWebSocket;